import { z } from 'zod';

// Schema للخطوة 1: معلومات العميل
export const clientInfoSchema = z.object({
  clientType: z.enum(['individual', 'b2b'], {
    required_error: 'يرجى اختيار نوع العميل',
  }),
  clientPhone: z.string().min(10, 'رقم الهاتف يجب أن يتكون من 10 أرقام على الأقل'),
  clientName: z.string().optional(),
  clientEmail: z.string().email('البريد الإلكتروني غير صحيح').optional().or(z.literal('')),
  clientAddress: z.string().optional(),
  companyId: z.string().optional(),
  companyName: z.string().optional(),
});

// Schema للخطوة 2: تفاصيل الرحلة
export const tripDetailsSchema = z.object({
  packageName: z.string().optional(),
  destination: z.string().min(1, 'يرجى اختيار الوجهة'),
  duration: z.string().min(1, 'يرجى اختيار تاريخ الوصول'),
  numberOfNights: z.number().min(1, 'يجب أن يكون عدد الليالي على الأقل 1'),
  adultsCount: z.number().min(1, 'يجب أن يكون عدد البالغين على الأقل 1'),
  childrenCount: z.number().min(0).optional(),
  infantsCount: z.number().min(0).optional(),
  numberOfRooms: z.number().min(1, 'يجب أن يكون عدد الغرف على الأقل 1'),
});

// Schema للفنادق
export const hotelSchema = z.object({
  hotelName: z.string().min(1, 'اسم الفندق مطلوب'),
  checkInDate: z.string().min(1, 'تاريخ الدخول مطلوب'),
  checkOutDate: z.string().min(1, 'تاريخ الخروج مطلوب'),
  roomType: z.string().min(1, 'نوع الغرفة مطلوب'),
  pricePerNight: z.number().min(0, 'السعر يجب أن يكون أكبر من أو يساوي 0'),
  nights: z.number().min(1, 'عدد الليالي يجب أن يكون على الأقل 1'),
});

// Schema للرحلات الجوية
export const flightSchema = z.object({
  flightType: z.enum(['international', 'domestic'], {
    required_error: 'نوع الرحلة مطلوب',
  }),
  airline: z.string().min(1, 'اسم شركة الطيران مطلوب'),
  departureCity: z.string().min(1, 'مدينة المغادرة مطلوبة'),
  arrivalCity: z.string().min(1, 'مدينة الوصول مطلوبة'),
  departureDate: z.string().min(1, 'تاريخ المغادرة مطلوب'),
  price: z.number().min(0, 'السعر يجب أن يكون أكبر من أو يساوي 0'),
});

// Schema للمواصلات
export const transportSchema = z.object({
  vehicleType: z.string().min(1, 'نوع المركبة مطلوب'),
  route: z.string().min(1, 'المسار مطلوب'),
  price: z.number().min(0, 'السعر يجب أن يكون أكبر من أو يساوي 0'),
});

// Schema للجولات
export const tourSchema = z.object({
  tourName: z.string().min(1, 'اسم الجولة مطلوب'),
  duration: z.string().min(1, 'مدة الجولة مطلوبة'),
  price: z.number().min(0, 'السعر يجب أن يكون أكبر من أو يساوي 0'),
});

// Schema للخدمات
export const serviceSchema = z.object({
  serviceName: z.string().min(1, 'اسم الخدمة مطلوب'),
  price: z.number().min(0, 'السعر يجب أن يكون أكبر من أو يساوي 0'),
});

// Schema للتسعير
export const pricingSchema = z.object({
  basePrice: z.number().min(0, 'السعر الأساسي يجب أن يكون أكبر من أو يساوي 0'),
  profitType: z.enum(['percentage', 'fixed']),
  profitValue: z.number().min(0, 'قيمة الربح يجب أن تكون أكبر من أو يساوي 0'),
  discount: z.number().min(0, 'الخصم يجب أن يكون أكبر من أو يساوي 0').optional(),
  taxes: z.number().min(0, 'الضرائب يجب أن تكون أكبر من أو يساوي 0').optional(),
  totalPrice: z.number().min(0, 'السعر الإجمالي يجب أن يكون أكبر من أو يساوي 0'),
});

// Validation functions
export const validateClientInfo = (data: any) => {
  try {
    clientInfoSchema.parse(data);
    return { success: true, errors: {} };
  } catch (error) {
    if (error instanceof z.ZodError) {
      const errors = error.errors.reduce((acc, err) => {
        const path = err.path.join('.');
        acc[path] = err.message;
        return acc;
      }, {} as Record<string, string>);
      return { success: false, errors };
    }
    return { success: false, errors: { general: 'حدث خطأ في التحقق من البيانات' } };
  }
};

export const validateTripDetails = (data: any) => {
  try {
    tripDetailsSchema.parse(data);
    return { success: true, errors: {} };
  } catch (error) {
    if (error instanceof z.ZodError) {
      const errors = error.errors.reduce((acc, err) => {
        const path = err.path.join('.');
        acc[path] = err.message;
        return acc;
      }, {} as Record<string, string>);
      return { success: false, errors };
    }
    return { success: false, errors: { general: 'حدث خطأ في التحقق من البيانات' } };
  }
};

export const validateHotel = (hotel: any) => {
  try {
    hotelSchema.parse(hotel);
    return { success: true, errors: {} };
  } catch (error) {
    if (error instanceof z.ZodError) {
      const errors = error.errors.reduce((acc, err) => {
        const path = err.path.join('.');
        acc[path] = err.message;
        return acc;
      }, {} as Record<string, string>);
      return { success: false, errors };
    }
    return { success: false, errors: { general: 'حدث خطأ في التحقق من البيانات' } };
  }
};

export const validateFlight = (flight: any) => {
  try {
    flightSchema.parse(flight);
    return { success: true, errors: {} };
  } catch (error) {
    if (error instanceof z.ZodError) {
      const errors = error.errors.reduce((acc, err) => {
        const path = err.path.join('.');
        acc[path] = err.message;
        return acc;
      }, {} as Record<string, string>);
      return { success: false, errors };
    }
    return { success: false, errors: { general: 'حدث خطأ في التحقق من البيانات' } };
  }
};

export const validatePricing = (data: any) => {
  try {
    pricingSchema.parse(data);
    return { success: true, errors: {} };
  } catch (error) {
    if (error instanceof z.ZodError) {
      const errors = error.errors.reduce((acc, err) => {
        const path = err.path.join('.');
        acc[path] = err.message;
        return acc;
      }, {} as Record<string, string>);
      return { success: false, errors };
    }
    return { success: false, errors: { general: 'حدث خطأ في التحقق من البيانات' } };
  }
};
